import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useGetFaqQuery, useGetFaqPublicQuery } from "../app/api/private/faq";
import { FaqCategory } from "../app/models/FaqCategory";
import { Accordion } from "../features/shared/Accordion";
import { Container } from "../features/shared/Container";
import { Loader } from "../features/shared/Loader";
import { Paper } from "../features/shared/Paper";
import { Markdown } from "../features/shared/Markdown";
import { MainPage } from "./shared/MainPage";
import { ContactCard } from "../features/shared/ContactCard";
import { Button } from "../features/shared/Button";

import { router } from "../app/services/router";

import { ReactComponent as Icon } from "./FaqPageIcon.svg";
import "./FaqPage.scss";

const classNamePrefix = "FaqPage";

type FaqPageProps = { isLoggedIn?: boolean };

export const FaqPage = (props: FaqPageProps) => {
  const { isLoggedIn } = props;

  const { currentData: faqData, error, isFetching } = useGetFaqQuery();
  const { currentData: faqDataPublic } = useGetFaqPublicQuery();

  const dataToDisplay: FaqCategory[] =
    (isLoggedIn ? faqData : faqDataPublic) ?? [];

  return faqData ? (
    <FaqPageContent data={dataToDisplay} isLoggedIn={isLoggedIn} />
  ) : (
    <Loader error={error} isFetching={isFetching} />
  );
};

type FaqPageContentProps = { data: FaqCategory[]; isLoggedIn?: boolean };

const FaqPageContent = (props: FaqPageContentProps) => {
  const { data: dataToDisplay, isLoggedIn } = props;

  const { t } = useTranslation();
  const { meta, title, contactCard } = t("pages.FaqPage") as unknown as {
    meta: { title: string };
    title: string;
    contactCard: {
      title: string;
      text: string;
      action: {
        messaging: string;
        interlocutors: string;
        info: string;
      };
    };
  };

  const hash = window.location.hash.slice(1);
  const hashItemId = hash.startsWith("faq-") ? hash.replace("faq-", "") : null;

  const categoryWithHashItem = hashItemId
    ? dataToDisplay.find((cat) =>
        cat.items.some((item) => item.id === hashItemId),
      )
    : null;

  return (
    <MainPage
      dataLayer={{
        categorie_page: "faq",
        nom_page: "faq",
        virtualpagetitle: "FAQ",
      }}
      icon={<Icon />}
      meta={meta}
      title={meta.title}
    >
      <Paper className={`${classNamePrefix}-title`}>
        <Markdown>{title}</Markdown>
      </Paper>
      <Container>
        <div className={`${classNamePrefix}-content`}>
          {dataToDisplay.map((category) => (
            <Accordion
              key={category.id}
              title={category.title}
              titleAs="h2"
              triggerLabel={`Expand ${category.title}`}
              className={`${classNamePrefix}-main-accordion`}
              defaultOpen={categoryWithHashItem?.id === category.id}
            >
              <div className={`${classNamePrefix}-sub-accordions`}>
                {category.items.map((item) => (
                  <div key={item.id} id={`faq-${item.id}`}>
                    <Accordion
                      title={item.question}
                      titleAs="h3"
                      triggerLabel={`Expand ${item.question}`}
                      className={`${classNamePrefix}-sub-accordion`}
                      defaultOpen={hashItemId === item.id}
                    >
                      <Paper className={`${classNamePrefix}-answer`}>
                        <div
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </Paper>
                    </Accordion>
                  </div>
                ))}
              </div>
            </Accordion>
          ))}
        </div>
        <div>
          <ContactCard
            title={contactCard.title}
            className={`${classNamePrefix}-ContactCard`}
          >
            <ContactCard.Item name={contactCard.text} label="" />
            {isLoggedIn && (
              <div>
                <Button
                  asChild
                  dataLayer={{
                    eventAction: "clic",
                    eventCategory: "faq",
                    eventLabel: "faq_messaging",
                  }}
                  variant="action"
                >
                  <Link to={router.getTo("mainMessaging")}>
                    {contactCard.action.messaging}
                  </Link>
                </Button>
                <Button
                  asChild
                  dataLayer={{
                    eventAction: "clic",
                    eventCategory: "faq",
                    eventLabel: "faq_interlocutors",
                  }}
                  variant="text"
                >
                  <Link to={router.getTo("mainContactInterlocutors")}>
                    {contactCard.action.interlocutors}
                  </Link>
                </Button>
              </div>
            )}
            {!isLoggedIn && <span>{contactCard.action.info}</span>}
          </ContactCard>
        </div>
      </Container>
    </MainPage>
  );
};
